import { SCORING_PRIORITY_COLUMNS } from "app/pages/sales-intelligence/modules/lead-scoring-agent/utils/scoringBoardUtils";
import {
    ScoringConfigType,
    type ScoringConfigRule,
} from "app/pages/sales-intelligence/sub-modules/signals/types/scoring-config";

export type ScoringScopeSegment = "all" | "private" | "shared" | "applicableForMe";

export type ScoringRulesView = {
    rules: ScoringConfigRule;
    ruleScopeById: Readonly<Record<string, ScoringConfigType>>;
};

const countRulesInConfig = (rules: ScoringConfigRule): number =>
    SCORING_PRIORITY_COLUMNS.reduce((count, priority) => count + (rules[priority]?.length ?? 0), 0);

export const buildScoringRulesView = (
    userRules: ScoringConfigRule,
    accountRules: ScoringConfigRule,
): ScoringRulesView => {
    const ruleScopeById: Record<string, ScoringConfigType> = {};
    const rules: ScoringConfigRule = {};

    for (const priority of SCORING_PRIORITY_COLUMNS) {
        const combinedColumn = [];

        for (const rule of userRules[priority] ?? []) {
            if (!rule.id) {
                continue;
            }

            ruleScopeById[rule.id] = ScoringConfigType.User;
            combinedColumn.push(rule);
        }

        for (const rule of accountRules[priority] ?? []) {
            if (!rule.id) {
                continue;
            }

            ruleScopeById[rule.id] = ScoringConfigType.Account;
            combinedColumn.push(rule);
        }

        if (combinedColumn.length > 0) {
            rules[priority] = combinedColumn;
        }
    }

    return { rules, ruleScopeById };
};

export const getScoringRulesForSegment = (
    segment: ScoringScopeSegment,
    userRules: ScoringConfigRule,
    accountRules: ScoringConfigRule,
    combinedView: ScoringRulesView,
    applicableForMeView?: ScoringRulesView,
): ScoringConfigRule => {
    if (segment === "private") {
        return userRules;
    }

    if (segment === "shared") {
        return accountRules;
    }

    if (segment === "applicableForMe") {
        return applicableForMeView?.rules ?? combinedView.rules;
    }

    return combinedView.rules;
};

export const countRulesInScoringView = (view: ScoringRulesView): number => countRulesInConfig(view.rules);

export const countScoringRulesByScope = (
    userRules: ScoringConfigRule,
    accountRules: ScoringConfigRule,
): { privateCount: number; sharedCount: number } => ({
    privateCount: countRulesInConfig(userRules),
    sharedCount: countRulesInConfig(accountRules),
});
